import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Logo } from '@/components/Logo';

export function PrivacyPage() {
  const { t } = useTranslation('privacy');

  const renderList = (items: string[]) => (
    <ul className="list-inside list-disc space-y-1.5">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );

  return (
    <div className="min-h-dvh bg-k-canvas">
      <nav className="border-b-2 border-brand-indigo bg-k-canvas/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9 text-brand-indigo" />
            <span className="font-display text-lg font-bold tracking-tight text-brand-indigo">
              AgentCAD
            </span>
          </Link>
          <LanguageSwitcher />
        </div>
      </nav>

      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1.5 text-sm font-bold text-brand-indigo transition-colors hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('backToTop')}
        </Link>

        <div className="rounded-2xl border-2 border-brand-indigo/15 bg-k-card p-6 sm:p-10">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-brand-indigo sm:text-4xl">
            {t('title')}
          </h1>
          <p className="mt-2 text-sm font-semibold text-brand-indigo/60">{t('lastUpdated')}</p>

          <div className="mt-10 space-y-8 text-base leading-relaxed text-k-ink/90">
            <section>
              <h2 className="mb-3 font-display text-xl font-bold text-brand-indigo">
                {t('sections.collect.heading')}
              </h2>
              <p className="mb-3">{t('sections.collect.intro')}</p>
              {renderList(t('sections.collect.items', { returnObjects: true }) as string[])}
            </section>

            <section>
              <h2 className="mb-3 font-display text-xl font-bold text-brand-indigo">
                {t('sections.purpose.heading')}
              </h2>
              {renderList(t('sections.purpose.items', { returnObjects: true }) as string[])}
            </section>

            <section>
              <h2 className="mb-3 font-display text-xl font-bold text-brand-indigo">
                {t('sections.thirdParty.heading')}
              </h2>
              <p>{t('sections.thirdParty.intro')}</p>
              <div className="mt-3">
                {renderList(t('sections.thirdParty.items', { returnObjects: true }) as string[])}
              </div>
            </section>

            <section>
              <h2 className="mb-3 font-display text-xl font-bold text-brand-indigo">
                {t('sections.storage.heading')}
              </h2>
              <p>{t('sections.storage.body')}</p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-xl font-bold text-brand-indigo">
                {t('sections.cookies.heading')}
              </h2>
              <p>{t('sections.cookies.body')}</p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-xl font-bold text-brand-indigo">
                {t('sections.rights.heading')}
              </h2>
              <p>{t('sections.rights.intro')}</p>
              <div className="mt-3">
                {renderList(t('sections.rights.items', { returnObjects: true }) as string[])}
              </div>
            </section>

            <section>
              <h2 className="mb-3 font-display text-xl font-bold text-brand-indigo">
                {t('sections.changes.heading')}
              </h2>
              <p>{t('sections.changes.body')}</p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-xl font-bold text-brand-indigo">
                {t('sections.contact.heading')}
              </h2>
              <p>
                {t('sections.contact.bodyPrefix')}
                <Link
                  to="/contact"
                  className="font-bold text-brand-indigo underline-offset-2 hover:underline"
                >
                  {t('sections.contact.link')}
                </Link>
                {t('sections.contact.bodySuffix')}
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
